import { useState, useEffect, Suspense, lazy } from "react";
import { Sidebar } from "./components/Sidebar";
import { BottomNavigation } from "./components/BottomNavigation";
import { MobileHeader } from "./components/MobileHeader";
// Lazy load components
const Dashboard = lazy(() => import("./components/Dashboard").then(module => ({ default: module.Dashboard })));
const BillingPage = lazy(() => import("./components/pages/BillingPage").then(module => ({ default: module.BillingPage })));
const OnlineUsers = lazy(() => import("./components/OnlineUsers").then(module => ({ default: module.OnlineUsers })));
const EmailVerificationPage = lazy(() => import("./components/pages/EmailVerificationPage").then(module => ({ default: module.EmailVerificationPage })));
const LandingPage = lazy(() => import("./components/LandingPage").then(module => ({ default: module.LandingPage })));
const LoginPage = lazy(() => import("./components/LoginPage").then(module => ({ default: module.LoginPage })));
const ForgotPasswordPage = lazy(() => import("./components/pages/ForgotPasswordPage").then(module => ({ default: module.ForgotPasswordPage })));
const LabRegistrationPage = lazy(() => import("./components/pages/LabRegistrationPage").then(module => ({ default: module.LabRegistrationPage })));
const BloodReportsPage = lazy(() => import("./components/pages/BloodReportsPage").then(module => ({ default: module.BloodReportsPage })));
const UrineReportsPage = lazy(() => import("./components/pages/UrineReportsPage").then(module => ({ default: module.UrineReportsPage })));
const StoolReportsPage = lazy(() => import("./components/pages/StoolReportsPage").then(module => ({ default: module.StoolReportsPage })));
const PatientsPage = lazy(() => import("./components/pages/PatientsPage").then(module => ({ default: module.PatientsPage })));
const ReportsHistoryPage = lazy(() => import("./components/pages/ReportsHistoryPage").then(module => ({ default: module.ReportsHistoryPage })));
const SettingsPage = lazy(() => import("./components/pages/SettingsPage").then(module => ({ default: module.SettingsPage })));
const SubscriptionPage = lazy(() => import("./components/pages/SubscriptionPage").then(module => ({ default: module.SubscriptionPage })));
const TeamPage = lazy(() => import("./components/pages/TeamPage").then(module => ({ default: module.TeamPage })));
const AcceptInvitationPage = lazy(() => import("./components/pages/AcceptInvitationPage").then(module => ({ default: module.AcceptInvitationPage })));
const SuperAdminDashboard = lazy(() => import("./components/pages/SuperAdminDashboard").then(module => ({ default: module.SuperAdminDashboard })));
const CreateTestPage = lazy(() => import("./components/pages/CreateTestPage").then(module => ({ default: module.CreateTestPage })));
const BarcodePage = lazy(() => import("./components/pages/BarcodePage").then(module => ({ default: module.BarcodePage })));
const DoctorsPage = lazy(() => import("./components/pages/DoctorsPage").then(module => ({ default: module.DoctorsPage })));
const PublicPages = lazy(() => import("./components/pages/PublicPages").then(module => ({ default: module.PublicPages })));
const AuditLogsPage = lazy(() => import("./components/pages/AuditLogsPage").then(module => ({ default: module.AuditLogsPage })));


import { UIScaleToggle } from "./components/UIScaleToggle";
import { ThemeToggle } from "./components/ThemeToggle";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useSubscription } from "./hooks/useSubscription";
import { UIScaleProvider } from "./contexts/UIScaleContext";
import {
  DatabaseProvider,
  useDatabase,
} from "./contexts/DatabaseContext";
import {
  getTestTemplatesFromFirestore as getTestTemplates,
  listPdfThemes,
  upsertPdfTheme,
} from "./services/firebaseDatabase";
import {
  defaultBloodTests,
  defaultUrineTests,
  defaultStoolTests,
} from "./constants/testTemplates";
import {
  calculateDashboardStats,
  formatReportsForDisplay,
  createInitialPatientInfo,
} from "./utils/dashboardHelpers";
import { Badge } from "./components/ui/badge";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import ScrollButton from "./components/ScrollButton";

import { Patient, Test, ReportData } from "./types/types";
import { logAction } from "./services/AuditService";

const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

function AppContent() {
  const { user, userProfile, loading: authLoading, currentLab } = useAuth();
  const { isExpired } = useSubscription();
  const {
    reports,
    addReport: addReportToDb,
    loading,
    syncing,
    offlineReports,
    syncOfflineData,
  } = useDatabase();

  const [activeTab, setActiveTab] =
    useState<string>("dashboard");
  const [showLogin, setShowLogin] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [patientInfo, setPatientInfo] = useState<Patient>(
    createInitialPatientInfo(),
  );
  const [publicPage, setPublicPage] = useState<string | null>(null);
  const [hasTheme, setHasTheme] = useState<boolean>(false);

  const [bloodTests, setBloodTests] = useState<Test[]>(
    defaultBloodTests.map((template) => ({
      ...template,
      id: Math.random().toString(36).substring(2, 15), // Generate a simple ID
    })),
  );
  const [urineTests, setUrineTests] = useState<Test[]>(
    defaultUrineTests.map((template) => ({
      ...template,
      id: Math.random().toString(36).substring(2, 15),
    })),
  );
  const [stoolTests, setStoolTests] = useState<Test[]>(
    defaultStoolTests.map((template) => ({
      ...template,
      id: Math.random().toString(36).substring(2, 15),
    })),
  );
  const [reportNotes, setReportNotes] = useState("");
  const [sampleStatus, setSampleStatus] = useState<'Collected' | 'Received' | 'Processing' | 'Completed' | 'Rejected'>('Collected');
  const [approvalStatus, setApprovalStatus] = useState<'Draft' | 'Pending' | 'Approved'>('Draft');

  // Reset handlers for clearing forms when creating a new report
  const clearPatientAndBloodTests = () => {
    setPatientInfo(createInitialPatientInfo());
    setBloodTests(
      defaultBloodTests.map((template) => ({
        ...template,
        id: Math.random().toString(36).substring(2, 15),
      })),
    );
    setReportNotes("");
    setSampleStatus('Collected');
    setApprovalStatus('Draft');
  };

  const clearPatientAndUrineTests = () => {
    setPatientInfo(createInitialPatientInfo());
    setUrineTests(
      defaultUrineTests.map((template) => ({
        ...template,
        id: Math.random().toString(36).substring(2, 15),
      })),
    );
    setReportNotes("");
    setSampleStatus('Collected');
    setApprovalStatus('Draft');
  };

  const clearPatientAndStoolTests = () => {
    setPatientInfo(createInitialPatientInfo());
    setStoolTests(
      defaultStoolTests.map((template) => ({
        ...template,
        id: Math.random().toString(36).substring(2, 15),
      })),
    );
    setReportNotes("");
    setSampleStatus('Collected');
    setApprovalStatus('Draft');
  };

  // Load test templates from database on mount
  // Load test templates from database on mount or when lab changes
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templates = await getTestTemplates(currentLab?.id);
        if (templates?.bloodTests && templates.bloodTests.length > 0) {
          setBloodTests(
            templates.bloodTests.map((template) => ({
              ...template,
              id: Math.random().toString(36).substring(2, 15),
            })),
          );
        }
        if (templates?.urineTests && templates.urineTests.length > 0) {
          setUrineTests(
            templates.urineTests.map((template) => ({
              ...template,
              id: Math.random().toString(36).substring(2, 15),
            })),
          );
        }
      } catch (error) {
        console.log("Using default templates:", error);
      }
    };
    loadTemplates();

  }, [currentLab]);

  // Check for PDF theme
  useEffect(() => {
    const checkTheme = async () => {
      if (currentLab?.id) {
        const themes = await listPdfThemes(currentLab.id);
        setHasTheme(themes.length > 0);
      }
    };
    checkTheme();
  }, [currentLab]);

  // Calculate dashboard stats and format reports
  const dashboardStats = calculateDashboardStats(reports);
  const recentReports = formatReportsForDisplay(reports);

  // Function to add a new report
  const addReport = async (reportData: ReportData) => {
    try {
      if (addReportToDb) {
        await addReportToDb({
          ...reportData,
          status: "Completed",
          createdBy: user?.email || "System",
        });

        if (currentLab?.id && user?.uid && user?.email) {
          logAction(
            currentLab.id,
            user.uid,
            user.email,
            "CREATE_REPORT",
            `Created ${reportData.type} for ${reportData.patientInfo.name}`
          );
        }
      }
    } catch (error) {
      console.error("Error saving report to database:", error);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const renderContent = (): JSX.Element => {
    switch (activeTab) {
      case "dashboard":
        return (
          <Dashboard
            recentReports={recentReports}
            dashboardStats={dashboardStats}
            onTabChange={setActiveTab}
          />
        );

      case "blood-reports":
        return (
          <BloodReportsPage
            patientInfo={patientInfo}
            onPatientInfoChange={setPatientInfo}
            bloodTests={bloodTests}
            onBloodTestChange={setBloodTests}
            onReportGenerated={() =>
              addReport({
                patientInfo,
                bloodTests,
                type: "Blood Report",
                notes: reportNotes,
                sampleStatus,
                approvalStatus,
                approvedBy: approvalStatus === 'Approved' ? user?.email || 'System' : undefined,
                approvedAt: approvalStatus === 'Approved' ? Date.now() : undefined,
              })
            }
            onClear={clearPatientAndBloodTests}
            notes={reportNotes}
            onNotesChange={setReportNotes}
            sampleStatus={sampleStatus}
            onSampleStatusChange={setSampleStatus}
            approvalStatus={approvalStatus}
            onApprovalStatusChange={setApprovalStatus}
            userRole={userProfile?.role}
          />
        );

      case "urine-reports":
        return (
          <UrineReportsPage
            patientInfo={patientInfo}
            onPatientInfoChange={setPatientInfo}
            urineTests={urineTests}
            onUrineTestChange={setUrineTests}
            onReportGenerated={() =>
              addReport({
                patientInfo,
                urineTests,
                type: "Urine Analysis",
                notes: reportNotes,
                sampleStatus,
                approvalStatus,
                approvedBy: approvalStatus === 'Approved' ? user?.email || 'System' : undefined,
                approvedAt: approvalStatus === 'Approved' ? Date.now() : undefined,
              })
            }
            onClear={clearPatientAndUrineTests}
            notes={reportNotes}
            onNotesChange={setReportNotes}
            sampleStatus={sampleStatus}
            onSampleStatusChange={setSampleStatus}
            approvalStatus={approvalStatus}
            onApprovalStatusChange={setApprovalStatus}
            userRole={userProfile?.role}
          />
        );

      case "stool-reports":
        return (
          <StoolReportsPage
            patientInfo={patientInfo}
            onPatientInfoChange={setPatientInfo}
            stoolTests={stoolTests}
            onStoolTestChange={setStoolTests}
            onReportGenerated={() =>
              addReport({
                patientInfo,
                stoolTests,
                type: "Stool Analysis",
                notes: reportNotes,
                sampleStatus,
                approvalStatus,
                approvedBy: approvalStatus === 'Approved' ? user?.email || 'System' : undefined,
                approvedAt: approvalStatus === 'Approved' ? Date.now() : undefined,
              })
            }
            onClear={clearPatientAndStoolTests}
            notes={reportNotes}
            onNotesChange={setReportNotes}
            sampleStatus={sampleStatus}
            onSampleStatusChange={setSampleStatus}
            approvalStatus={approvalStatus}
            onApprovalStatusChange={setApprovalStatus}
            userRole={userProfile?.role}
          />
        );

      case "billing":
        return <BillingPage />;

      case "patients":
        return (
          <PatientsPage
            dashboardStats={dashboardStats}
            reportsCount={reports.length}
          />
        );

      case "reports-history":
        return (
          <ReportsHistoryPage
            recentReports={recentReports}
            loading={loading}
          />
        );



      case "settings":
        return <SettingsPage reportsCount={reports.length} />;

      case "subscription":
        return <SubscriptionPage />;

      case "team":
        return <TeamPage />;

      case "create-test":
        return (
          <CreateTestPage
            patientInfo={patientInfo}
            onPatientInfoChange={setPatientInfo}
            onTabChange={setActiveTab}
            onClear={() => setPatientInfo(createInitialPatientInfo())}
          />
        );

      case "barcode-generator":
        return <BarcodePage />;

      case "doctors":
        return <DoctorsPage />;

      case "audit-logs":
        return <AuditLogsPage />;

      default:
        return (
          <Dashboard
            recentReports={recentReports}
            dashboardStats={dashboardStats}
            onTabChange={setActiveTab}
          />
        );
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Check for invitation route (simple URL check since we don't have a full router yet)
  const isInviteRoute = window.location.pathname === '/accept-invite';
  if (isInviteRoute) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <AcceptInvitationPage />
      </Suspense>
    );
  }

  // Super Admin Route
  const isSuperAdminRoute = window.location.pathname === '/super-admin';
  if (isSuperAdminRoute && userProfile?.role === 'super_admin') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <SuperAdminDashboard />
      </Suspense>
    );
  }
  // Handle public page navigation
  const handleNavigate = (page: string | null) => {
    if (page === 'features') {
      setPublicPage(null);
      // Allow time for LandingPage to mount if we're coming from another page
      setTimeout(() => {
        const element = document.getElementById('features');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      setPublicPage(page);
      window.scrollTo(0, 0);
    }
  };

  // Show landing page if not authenticated
  if (!user) {
    if (showForgotPassword) {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <ForgotPasswordPage
            onBack={() => setShowForgotPassword(false)}
          />
        </Suspense>
      );
    }

    if (showLogin) {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <LoginPage
            onBack={() => setShowLogin(false)}
            onLoginSuccess={() => setShowLogin(false)}
            onForgotPassword={() => setShowForgotPassword(true)}
          />
        </Suspense>
      );
    }

    if (publicPage) {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <PublicPages
            page={publicPage}
            onNavigate={handleNavigate}
            onGetStarted={() => {
              setPublicPage(null);
              setShowLogin(true);
            }}
          />
        </Suspense>
      );
    }

    return (
      <Suspense fallback={<LoadingFallback />}>
        <LandingPage
          onGetStarted={() => setShowLogin(true)}
          onNavigate={handleNavigate}
        />
      </Suspense>
    );
  }

  // Enforce Email Verification
  if (!user.emailVerified) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <EmailVerificationPage />
      </Suspense>
    );
  }

  // Show Lab Registration if user is logged in but has no lab
  if (!currentLab) {
    // If user is super admin, redirect to super admin dashboard instead of forcing registration
    if (userProfile?.role === 'super_admin') {
      if (window.location.pathname !== '/super-admin') {
        window.location.href = '/super-admin';
        return <LoadingFallback />;
      }
    }

    return (
      <Suspense fallback={<LoadingFallback />}>
        <LabRegistrationPage />
      </Suspense>
    );
  }

  // Block access if subscription is expired (unless they are already on the subscription page logic, 
  // but here we just force the view. Ideally we'd have a router.)
  if (isExpired) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="flex justify-end p-4">
          <ThemeToggle />
        </div>
        <Suspense fallback={<LoadingFallback />}>
          <SubscriptionPage />
        </Suspense>
      </div>
    );
  }

  // Main authenticated app
  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Layout */}
      <div className="hidden md:flex h-screen">
        <Sidebar
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <main className="flex-1 flex flex-col overflow-hidden">
          <Suspense fallback={<div className="h-12" />}>
            {/* EmailVerificationBanner removed - verification is now enforced via blocking page */}
          </Suspense>
          <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
            <div className="flex justify-between items-center p-4">
              {/* Sync Status */}
              <div className="flex items-center space-x-2">
                {syncing && (
                  <Badge variant="secondary" className="text-xs">
                    <div className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin mr-1"></div>
                    Syncing...
                  </Badge>
                )}
                {offlineReports.length > 0 && (
                  <Badge variant="outline" className="text-xs cursor-pointer" onClick={syncOfflineData}>
                    {offlineReports.length} offline reports
                  </Badge>
                )}
              </div>

              {/* Online Users, UI Scale Toggle and Theme Toggle */}
              <div className="flex items-center space-x-3">
                <OnlineUsers />
                <UIScaleToggle />
                <ThemeToggle />
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-4 md:p-6">
            <Suspense fallback={<LoadingFallback />}>
              {renderContent()}
            </Suspense>
          </div>
        </main>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden">
        <div className="flex flex-col min-h-screen">
          <MobileHeader
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {/* Sync Status and Controls for Mobile */}
          {(syncing || offlineReports.length > 0) && (
            <div className="px-4 py-2 bg-muted/50 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {syncing && (
                    <Badge variant="secondary" className="text-xs">
                      <div className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin mr-1"></div>
                      Syncing...
                    </Badge>
                  )}
                  {offlineReports.length > 0 && (
                    <Badge variant="outline" className="text-xs cursor-pointer" onClick={syncOfflineData}>
                      {offlineReports.length} offline
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}

          <main className="flex-1 overflow-auto pb-20">
            <Suspense fallback={<LoadingFallback />}>
              <div className="p-4 max-w-full">{renderContent()}</div>
            </Suspense>
          </main>

          <BottomNavigation
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </div>
      </div>

      <Toaster />
      <ScrollButton />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <UIScaleProvider>
        <DatabaseProvider>
          <AppContent />
        </DatabaseProvider>
      </UIScaleProvider>
    </AuthProvider>
  );
}